// Single source of truth for the UI (E-12): project state (persisted, undoable) plus transient view state.

import type { Project, SourceId, UserSettings } from '../core/types';
import { createProject, defaultSettings } from '../core/project';
import { rescaleTickRange } from '../core/timeline';

export type DockTab = 'info' | 'check' | 'adjust' | 'analysis';

export interface UiState {
  playing: boolean;
  /** Signed playback rate multiplier (J/K/L shuttle), independent of project speed. */
  shuttle: number;
  dockOpen: boolean;
  dockTab: DockTab;
  levelTarget: 'rgb' | 'r' | 'g' | 'b';
  alignOpen: boolean;
  /** Two-point alignment in progress (F-03.11): points on A (A pixels) and on the version (its own pixels). */
  alignPick: { id: SourceId; a: { x: number; y: number }[]; b: { x: number; y: number }[] } | null;
  tempFull: SourceId | null;
  flickerShowB: boolean;
  readOnly: boolean;
  saveHandleName: string | null;
  dirty: boolean;
}

export interface AppState {
  project: Project;
  ui: UiState;
  settings: UserSettings;
}

type Listener = (s: AppState) => void;

function defaultUi(): UiState {
  return {
    playing: false,
    shuttle: 1,
    dockOpen: true,
    dockTab: 'info',
    levelTarget: 'rgb',
    alignOpen: false,
    alignPick: null,
    tempFull: null,
    flickerShowB: false,
    readOnly: false,
    saveHandleName: null,
    dirty: false,
  };
}

export class Store {
  state: AppState;
  private listeners = new Set<Listener>();
  private undoStack: { project: Project; key?: string; at: number }[] = [];
  private redoStack: Project[] = [];
  private scheduled = false;
  onProjectChange: (() => void) | null = null;
  /** Called after undo/redo so the timeline and frame requests follow the restored state. */
  onRestore: (() => void) | null = null;

  constructor(appVersion: string) {
    this.state = {
      project: createProject('', appVersion),
      ui: defaultUi(),
      settings: defaultSettings(navigator.language),
    };
  }

  subscribe(f: Listener): () => void {
    this.listeners.add(f);
    return () => this.listeners.delete(f);
  }

  /** Schedules listeners on the next animation frame (coalesces many updates). */
  notify(): void {
    if (this.scheduled) return;
    this.scheduled = true;
    requestAnimationFrame(() => {
      this.scheduled = false;
      for (const f of this.listeners) f(this.state);
    });
  }

  /** Transient UI change: not undoable, not persisted in the project. */
  ui(mut: (ui: UiState) => void): void {
    mut(this.state.ui);
    this.notify();
  }

  settings(mut: (s: UserSettings) => void): void {
    mut(this.state.settings);
    this.notify();
  }

  /**
   * Project change. Undoable changes snapshot the project first; consecutive changes with the same mergeKey
   * within 800 ms collapse into one undo step (slider drags, nudges).
   */
  project(mut: (p: Project) => void, opts: { undo?: boolean; mergeKey?: string } = { undo: true }): void {
    if (this.state.ui.readOnly && opts.undo !== false) {
      // Read-only projects (newer schema) still allow viewing changes but never mark dirty.
    }
    if (opts.undo !== false) {
      const now = performance.now();
      const top = this.undoStack[this.undoStack.length - 1];
      if (!(opts.mergeKey && top && top.key === opts.mergeKey && now - top.at < 800)) {
        this.undoStack.push({ project: structuredClone(this.state.project), key: opts.mergeKey, at: now });
        if (this.undoStack.length > 200) this.undoStack.shift();
      } else top.at = now;
      this.redoStack = [];
    }
    mut(this.state.project);
    this.state.ui.dirty = true;
    this.onProjectChange?.();
    this.notify();
  }

  /** Replaces the whole project (open / restore); clears history. */
  replaceProject(p: Project): void {
    this.state.project = p;
    this.undoStack = [];
    this.redoStack = [];
    this.notify();
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }
  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  undo(): boolean {
    const prev = this.undoStack.pop();
    if (!prev) return false;
    this.redoStack.push(structuredClone(this.state.project));
    this.restoreKeepingRuntime(prev.project);
    return true;
  }

  redo(): boolean {
    const next = this.redoStack.pop();
    if (!next) return false;
    this.undoStack.push({ project: structuredClone(this.state.project), at: 0 });
    this.restoreKeepingRuntime(next);
    return true;
  }

  /** Undo must not move the playhead or unload sources: keep playback tick and source list from the present. */
  private restoreKeepingRuntime(p: Project): void {
    const cur = this.state.project;
    p.playback.tick = cur.playback.tick;
    const ids = new Set(cur.sources.map((s) => s.id));
    p.sources = p.sources.filter((s) => ids.has(s.id));
    for (const s of cur.sources) if (!p.sources.find((x) => x.id === s.id)) p.sources.push(s);
    // The loaded files are not part of undo: keep the current file identity, spec and grid scale.
    for (const s of p.sources) {
      const c = cur.sources.find((x) => x.id === s.id)!;
      s.file = c.file;
      s.spec = c.spec;
    }
    p.compareScale = cur.compareScale;
    if ((p.tickScale || 1) !== (cur.tickScale || 1)) {
      // The snapshot was taken at another tick rate (an interpolated B was added or removed since).
      if (p.playback.inPoint !== undefined) p.playback.inPoint = rescaleTickRange(p.playback.inPoint, p.playback.inPoint, p.tickScale || 1, cur.tickScale || 1).start;
      if (p.playback.outPoint !== undefined) p.playback.outPoint = rescaleTickRange(p.playback.outPoint, p.playback.outPoint, p.tickScale || 1, cur.tickScale || 1).end;
    }
    p.tickScale = cur.tickScale;
    this.state.project = p;
    this.state.ui.dirty = true;
    this.onProjectChange?.();
    this.onRestore?.();
    this.notify();
  }
}
